import React from 'react'
import { observer } from 'mobx-react'
import ManageItemsViewModel, { ManageItemsMode } from './ManageItemsViewModel'
import styles from './ManageItemsViewModel.sass'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetContentBlock,
} from 'components/ModalSheet'
import Button from 'components/Button'
import { RadioButtonGroup, RadioButtonItem } from 'components/RadioButtonGroup'
import { match } from 'utils/pattern-match-util'
import { Form, FormGroupContainer, FormRow, Help } from 'components/inputs'
import {
  CustomSelect,
  MultiItemSelectedDefault,
  useCustomSelectStateFromInfiniteScrollProvider,
} from 'components/inputs/CustomSelect'
import SearchResultItem from 'components/inputs/CustomSelect/SearchResultItem'
import {
  JobScopeCustomItem,
  JobScopeItemSource,
} from '../UpdateJobScopeViewModel'
import { IconButton } from 'components/IconButton'
import Icon from 'components/Icon'
import cx from 'classnames'
import { asNumber } from 'utils/number-util'

interface ManageItemsViewModelProps {
  vm: ManageItemsViewModel
}

export default observer(function ManageItemsViewModel({
  vm,
}: ManageItemsViewModelProps) {
  const inventoryItemProps = useCustomSelectStateFromInfiniteScrollProvider({
    scrollProvider: vm.inventoryItemScrollProvider,
    searchResults: vm.inventoryItemScrollProvider.items.slice(),
    searchText: vm.searchText.value,
    onSearchTextChange: vm.searchText.change,
  })

  return (
    <ModalSheet
      disableCloseOnOutsideClick
      showing={vm.showing}
      onRequestClose={vm.close}
    >
      <ModalSheetHeader withCloseButton bordered>
        <div className={styles.modalSheetTitle}>Add more services</div>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetContentBlock expanded>
          <RadioButtonGroup label="What would you like to add?" size="big-wide">
            <RadioButtonItem
              onClick={() => vm.onChangeMode(ManageItemsMode.AnExistingService)}
              active={vm.mode === ManageItemsMode.AnExistingService}
              dataTest="job-scope-update-an-existing-service-button"
            >
              An existing service
            </RadioButtonItem>
            <RadioButtonItem
              onClick={() => vm.onChangeMode(ManageItemsMode.NewCustomService)}
              active={vm.mode === ManageItemsMode.NewCustomService}
              dataTest="job-scope-update-new-custom-service-button"
            >
              New custom service
            </RadioButtonItem>
          </RadioButtonGroup>
        </ModalSheetContentBlock>

        <ModalSheetContentBlock expanded>
          {match(vm.mode, {
            [ManageItemsMode.AnExistingService]: () => (
              <Form>
                <FormRow>
                  <FormGroupContainer
                    label="Service item"
                    help="Select all service items that you would like to add"
                  >
                    <CustomSelect
                      {...inventoryItemProps}
                      placeholder="Select service items"
                      itemToString={(v) => String(v?.name)}
                      multi
                      persist
                      value={vm.inventoryItems}
                      onChange={vm.setInventoryItems}
                      renderItem={(itemProps) => (
                        <MultiItemSelectedDefault
                          {...itemProps}
                          item={itemProps}
                          itemToString={(i) => i.item?.name}
                        />
                      )}
                      renderSearchItem={({
                        item,
                        itemToString,
                        ...itemProps
                      }) => (
                        <SearchResultItem
                          {...itemProps}
                          data-test={
                            'job-scope-update-inventory-item-option-' +
                            item.name.toLowerCase()
                          }
                        >
                          <>
                            <div className={styles.itemName}>{item.name}</div>
                            <div className={styles.itemDescription}>
                              {item.description}
                            </div>
                          </>
                        </SearchResultItem>
                      )}
                      data-test="job-scope-update-inventory-item-dropdown"
                    />
                  </FormGroupContainer>
                </FormRow>
              </Form>
            ),
            [ManageItemsMode.NewCustomService]: () => (
              <Form>
                <FormRow>
                  <FormGroupContainer>
                    {vm.selectedCustomItems.length === 0 && (
                      <div className={styles.customItemsEmptyState}>
                        <div className={styles.customItemsEmptyStateText}>
                          There are no custom items on this job.
                        </div>
                      </div>
                    )}

                    {vm.selectedCustomItems.length > 0 && (
                      <div className={styles.newCustomServiceHeaderContainer}>
                        <div className={styles.newCustomServiceHeader}>
                          <span className={styles.customItemNameHeader}>
                            Custom name
                          </span>
                        </div>
                        <div className={styles.newCustomServiceHeader}>
                          <span className={styles.customItemClientFeeHeader}>
                            Client fee
                          </span>
                          <span className={styles.customItemProCutHeader}>
                            Pro cut
                          </span>
                        </div>
                      </div>
                    )}
                    {vm.selectedCustomItems.map((item) => (
                      <ManageCustomItem vm={vm} item={item} key={item.id} />
                    ))}

                    <Button
                      type="text"
                      onClick={() => vm.createCustomItem()}
                      dataTest="job-scope-update-manage-items-add-another-button"
                    >
                      + Add another
                    </Button>
                  </FormGroupContainer>
                </FormRow>
              </Form>
            ),
          })}
        </ModalSheetContentBlock>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            type="default"
            onClick={vm.close}
            dataTest="job-scope-update-manage-items-close-button"
          >
            Close
          </Button>
          <Button
            type="primary"
            onClick={vm.submit}
            dataTest="job-scope-update-manage-items-submit-button"
          >
            Add Services
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface ManageCustomItemsProps {
  vm: ManageItemsViewModel
  item: JobScopeCustomItem
}

const ManageCustomItem = observer(function ManageCustomItems({
  vm,
  item,
}: ManageCustomItemsProps) {
  const isFromJob = item.source === JobScopeItemSource.Job

  return (
    <div className={styles.customItemContainer}>
      <div className={styles.customItemInputContainer}>
        <div className={styles.customItemNameInput}>
          <input
            type="text"
            className={cx(
              styles.input,
              styles.customItemName,
              item.validation.getError('name') && styles.hasError,
            )}
            placeholder="Enter your service type"
            value={item.name}
            onChange={(e) => item.setName(e.target.value)}
            disabled={isFromJob}
            data-test={`job-scope-update-name-input-${item.name}`}
          />
          <Help error={item.validation.getError('name')} />
        </div>
        <CustomItemMoneyInput
          item={item}
          field="price"
          disabled={isFromJob}
          dataTest={`job-scope-update-price-input-${item.name}`}
        />
        <CustomItemMoneyInput
          item={item}
          field="proPrice"
          disabled={isFromJob}
          dataTest={`job-scope-update-pro-price-input-${item.name}`}
        />
      </div>
      <IconButton
        className={cx(
          styles.removeItemButton,
          item.validation.getError('name') && styles.hasNameValidationError,
        )}
        onClick={() => vm.removeSelectedCustomItem(item)}
        dataTest={`job-scope-update-manage-items-remove-button-${item.name}`}
      >
        <Icon name="close" size={24} />
      </IconButton>
    </div>
  )
})

interface CustomItemMoneyInputProps {
  item: JobScopeCustomItem
  field: 'price' | 'proPrice'
  disabled?: boolean
  dataTest?: string
}

const CustomItemMoneyInput = observer(function CustomItemMoneyInput({
  item,
  field,
  disabled = false,
  dataTest,
}: CustomItemMoneyInputProps) {
  return (
    <div className={styles.customItemPriceInput}>
      <div className={styles.customItemPriceInputContainer}>
        <span className={styles.customItemPriceDollarSign}>$</span>
        <input
          type="number"
          disabled={disabled}
          className={cx(
            styles.customItemMoneyInput,
            field === 'price'
              ? styles.customItemClientFee
              : styles.customItemProCut,
          )}
          placeholder="0.00"
          step="0.01"
          value={item[field]}
          onChange={(e) =>
            field === 'price'
              ? item.setPrice(asNumber(e.target.value))
              : item.setProPrice(asNumber(e.target.value))
          }
          data-test={dataTest}
        />
      </div>
    </div>
  )
})
